import { useEffect, useState } from 'react';
import { isCell } from '@/lib/cell/config';

/**
 * THE PLATFORM FREEZE (R0c, item 75d9dc76): while production is read-only for the AWS cutover, the Pro Hub shows the
 * move's banner. The flag lives in the database (image-studios-backend 20261109500000_platform_freeze.sql), read through
 * public.platform_freeze_status(), which every client may call. DARK BY CONSTRUCTION: before the migration exists the
 * RPC is missing and the read fails, which reads as not frozen. On the cell build there is no Supabase; the cell's
 * frozen state is the window itself (the runbook), so nothing is read there.
 */
export type FreezeRead = () => Promise<{ until: string | null } | null>;

export const readFreeze: FreezeRead = async () => {
  if (isCell()) return null;
  try {
    // Loaded when first read, so importing the Layout (and its tests) never builds the Supabase client.
    const { supabase } = await import('@/lib/supabase');
    const { data, error } = await supabase.rpc('platform_freeze_status');
    const row = Array.isArray(data) ? data[0] : data;
    if (error || !row || (row as { frozen?: unknown }).frozen !== true) return null;
    return { until: (row as { until?: string | null }).until ?? null };
  } catch {
    return null;
  }
};

/** Read on mount and every minute; undefined while not frozen. */
export function usePlatformFreeze(read: FreezeRead = readFreeze, everyMs = 60_000): { until: string | null } | undefined {
  const [notice, setNotice] = useState<{ until: string | null } | undefined>(undefined);
  useEffect(() => {
    let live = true;
    const tick = async () => { const n = await read(); if (live) setNotice(n ?? undefined); };
    void tick();
    const t = setInterval(() => { void tick(); }, everyMs);
    return () => { live = false; clearInterval(t); };
  }, [read, everyMs]);
  return notice;
}
